import _ from 'lodash';
import {
  ActivityGroupModel,
  InspectionSignatureReference,
  OrderModel,
  OrderView,
  RoutingItemModel,
  RoutingItemStepModel,
} from '../../OrderDocumentation';
import { Signature } from '../../Signature';
import { cloneOrThrow, findNextKey, getIndexOfAfterKey } from './cloneHelpers';
import { EventEmitter } from 'events';
import { ActivityModel, GenericActivity } from '../../Activities';
import { genericActivities } from '../../transformers/genericActivitiesDatabase';
import { OrderModel2View } from '../../transformers/viewFactory';

type Events = {
  onReceive: [{ type: 'resourceUpdate'; data: OrderView }];
};
export type SaveFile = {
  order: OrderModel;
  routingItems: RoutingItemModel[];
  routingItemSteps: RoutingItemStepModel[];
  activityGroups: ActivityGroupModel[];
  activities: ActivityModel[];
  modelFile: string;
};

export class MockOrderService extends EventEmitter<Events> {
  constructor(
    private activeOrder: OrderModel,
    private activeRoutingItems: RoutingItemModel[],
    private activeRoutingItemSteps: RoutingItemStepModel[],
    private activeActivityGroups: ActivityGroupModel[],
    private activeActivities: ActivityModel[],
    private activeModelFile: string,
    activeGenericActivities?: Record<string, GenericActivity>,
    private constantDelay?: number,
  ) {
    super();

    // push first update
    this.sendUpdate();
  }

  private sendUpdate() {
    this.fakeDelay().then(() => {
      this.emit('onReceive', {
        type: 'resourceUpdate',
        data: OrderModel2View(
          this.activeOrder,
          this.activeRoutingItems,
          this.activeRoutingItemSteps,
          this.activeActivityGroups,
          this.activeActivities,
          genericActivities,
        ),
      });
    });
  }
  private putOrder(newOrder: OrderModel) {
    this.activeOrder = newOrder;
    this.sendUpdate();
  }
  private putRoutingItem(newRoutingItem: RoutingItemModel) {
    this.activeRoutingItems = [...this.activeRoutingItems.filter((i) => i.key !== newRoutingItem.key), newRoutingItem];
    this.sendUpdate();
  }
  private putRoutingItemStep(newRoutingItemStep: RoutingItemStepModel) {
    this.activeRoutingItemSteps = [
      ...this.activeRoutingItemSteps.filter((i) => i.key !== newRoutingItemStep.key),
      newRoutingItemStep,
    ];
    this.sendUpdate();
  }
  private putActivityGroup(newActivityGroup: ActivityGroupModel) {
    this.activeActivityGroups = [
      ...this.activeActivityGroups.filter((i) => i.key !== newActivityGroup.key),
      newActivityGroup,
    ];
    this.sendUpdate();
  }
  private putActivity(newActivity: ActivityModel) {
    this.activeActivities = [...this.activeActivities.filter((i) => i.key !== newActivity.key), newActivity];
    this.sendUpdate();
  }

  async fakeDelay(delay?: number) {
    if (delay === undefined) {
      delay = this.constantDelay;
    }
    if (delay === undefined) {
      delay = Math.random() * 1000;
    }
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  async editStep(
    stepKey: string,
    data: Pick<RoutingItemStepModel, 'name' | 'description' | 'depends' | 'content'>,
    afterStepKey: string,
  ) {
    const step = cloneOrThrow(this.activeRoutingItemSteps, stepKey);
    step.description = data.description;
    step.name = data.name;
    step.content = _.cloneDeep(data.content);

    // Move to the correct position
    step.belongsToPosition = getIndexOfAfterKey(this.activeRoutingItemSteps, afterStepKey);

    this.putRoutingItemStep(step);

    return this.fakeDelay();
  }
  async editActivityGroup(
    activityGroupKey: string,
    data: Pick<ActivityGroupModel, 'name' | 'description' | 'content'>,
    afterActivityGroupKey: string,
  ) {
    const activityGroup = cloneOrThrow(this.activeActivityGroups, activityGroupKey);

    activityGroup.name = data.name;
    activityGroup.description = data.description;
    activityGroup.content = _.cloneDeep(data.content);

    // Move to the correct position
    activityGroup.belongsToPosition = getIndexOfAfterKey(this.activeActivityGroups, afterActivityGroupKey);

    this.putActivityGroup(activityGroup);
    return this.fakeDelay();
  }

  async editActivity(
    key: string,
    data: Pick<
      ActivityModel,
      'name' | 'genericActivityKey' | 'customAttributes' | 'content' | 'count' | 'description' | 'subjects'
    >,
    afterActivityKey: string,
  ) {
    const activity = cloneOrThrow(this.activeActivities, key);

    activity.name = data.name;
    activity.description = data.description;
    activity.genericActivityKey = data.genericActivityKey;
    activity.customAttributes = _.cloneDeep(data.customAttributes);
    activity.content = _.cloneDeep(data.content);
    activity.subjects = _.cloneDeep(data.subjects);

    // Move to the correct position
    activity.belongsToPosition = getIndexOfAfterKey(this.activeActivities, afterActivityKey);

    this.putActivity(activity);
    return this.fakeDelay();
  }

  async deleteStep(key: string) {
    this.activeRoutingItemSteps = this.activeRoutingItemSteps.filter((i) => i.key !== key);
    this.sendUpdate();
    return this.fakeDelay();
  }
  async deleteActivityGroup(key: string) {
    this.activeActivityGroups = this.activeActivityGroups.filter((i) => i.key === key);
    this.sendUpdate();
    return this.fakeDelay();
  }
  async deleteActivity(key: string) {
    this.activeActivities = this.activeActivities.filter((i) => i.key !== key);
    this.sendUpdate();
    return this.fakeDelay();
  }
  async createStep(
    routingItemKey: string,
    data: Pick<RoutingItemStepModel, 'name' | 'description'>,
    afterStepKey: string,
  ) {
    const step: RoutingItemStepModel = {
      key: findNextKey(this.activeRoutingItemSteps, 'RoutingItemStep'),

      progress: 0,
      depends: [],
      status: 'toStart',

      belongsToKey: routingItemKey,
      belongsToPosition: getIndexOfAfterKey(this.activeRoutingItemSteps, afterStepKey),

      name: data.name,
      description: data.description,
      content: {
        text: ['Empty step'],
      },
    };

    // submit
    this.putRoutingItemStep(step);
    return this.fakeDelay();
  }
  async createActivityGroup(
    stepKey: string,
    data: Pick<ActivityGroupModel, 'name' | 'description'>,
    afterActivityGroupKey: string,
  ) {
    const activityGroup: ActivityGroupModel = {
      key: findNextKey(this.activeActivityGroups, 'ActivityGroup'),

      belongsToKey: stepKey,
      belongsToPosition: getIndexOfAfterKey(this.activeActivityGroups, afterActivityGroupKey),

      name: data.name,
      description: data.description,
      content: { text: ['Some content'] },

      performed: null,
      checked: null,
    };
    // submit
    this.putActivityGroup(activityGroup);
    return this.fakeDelay();
  }
  async createActivity(
    activityGroupKey: string,
    data: Pick<ActivityModel, 'name' | 'genericActivityKey' | 'description'>,
    afterActivityKey: string,
  ) {
    const ag = this.activeActivityGroups.find((k) => (k.key = activityGroupKey));
    if (!ag) {
      throw new Error('Should have activity group');
    }

    const activity: ActivityModel = {
      key: findNextKey(this.activeActivities, 'Activity'),
      belongsToKey: activityGroupKey,
      belongsToPosition: getIndexOfAfterKey(this.activeActivities, afterActivityKey),

      name: data.name,
      description: data.description,
      content: _.cloneDeep(ag.content),

      genericActivityKey: data.genericActivityKey,

      count: 1,
      subjects: [],
      customAttributes: {},
    } as ActivityModel;

    // submit
    this.putActivity(activity);
    return this.fakeDelay();
  }

  async loadFromFile(saveFile: SaveFile) {
    this.activeOrder = _.cloneDeep(saveFile.order);
    this.activeRoutingItems = _.cloneDeep(saveFile.routingItems);
    this.activeRoutingItemSteps = _.cloneDeep(saveFile.routingItemSteps);
    this.activeActivityGroups = _.cloneDeep(saveFile.activityGroups);
    this.activeActivities = _.cloneDeep(saveFile.activities);
    this.activeModelFile = saveFile.modelFile;
    this.sendUpdate();
    return this.fakeDelay();
  }
  async createSaveFile(): Promise<SaveFile> {
    await this.fakeDelay();
    return {
      order: this.activeOrder,
      routingItems: this.activeRoutingItems,
      routingItemSteps: this.activeRoutingItemSteps,
      activityGroups: this.activeActivityGroups,
      activities: this.activeActivities,
      modelFile: this.activeModelFile,
    };
  }

  async replaceModel(modelFile: string) {
    this.activeModelFile = modelFile;
    this.sendUpdate();
    return this.fakeDelay();
  }

  async recordSign(reference: InspectionSignatureReference, data: Omit<Signature, 'publicHash'>) {
    const parent = cloneOrThrow(this.activeActivityGroups, reference.activityGroupKey);

    if (reference.item === 'checked') {
      parent.checked = { ...data, publicHash: 'someHash' };
    } else {
      parent.performed = { ...data, publicHash: 'SomeHash' };
    }
    this.putActivityGroup(parent);
    return this.fakeDelay();
  }

  getAvailableGenericActivities(): Record<string, GenericActivity> {
    return genericActivities;
  }
}
